import React, { useCallback } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  FooterAction,
  Icons,
} from '@ohif/ui-next';

interface DeleteConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  hasError: boolean;
  isDeleting: boolean;
  onConfirm: () => void;
  onCloseAutoFocus?: (event: Event) => void;
}

export default function DeleteConfirmDialog({
  open,
  onOpenChange,
  hasError,
  isDeleting,
  onConfirm,
  onCloseAutoFocus,
}: DeleteConfirmDialogProps) {
  const confirmIfEnabled = useCallback(() => {
    if (!isDeleting) {
      onConfirm();
    }
  }, [isDeleting, onConfirm]);

  const cancelIfEnabled = useCallback(() => {
    if (!isDeleting) {
      onOpenChange(false);
    }
  }, [isDeleting, onOpenChange]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        className="sm:max-w-md"
        data-cy="sycai-delete-confirm-dialog"
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogHeader>
          <DialogTitle>Delete study</DialogTitle>
          <DialogDescription className="text-white">
            This permanently deletes the study. This cannot be undone.
          </DialogDescription>
        </DialogHeader>

        {hasError && (
          <div
            className="text-destructive text-sm"
            data-cy="sycai-delete-error"
          >
            Could not delete this study. Please try again.
          </div>
        )}

        <FooterAction className="gap-3 pt-1">
          <FooterAction.Auxiliary
            className="hover:text-foreground/60 flex-1 hover:bg-transparent aria-disabled:opacity-50"
            dataCY="sycai-delete-cancel"
            onClick={cancelIfEnabled}
            aria-disabled={isDeleting || undefined}
          >
            Cancel
          </FooterAction.Auxiliary>
          <FooterAction.Primary
            className="flex-1 bg-red-800 text-white hover:bg-red-700 aria-disabled:opacity-50"
            dataCY="sycai-delete-confirm"
            onClick={confirmIfEnabled}
            aria-disabled={isDeleting || undefined}
          >
            {isDeleting && <Icons.LoadingSpinner className="mr-1" />}
            Delete
          </FooterAction.Primary>
        </FooterAction>
      </DialogContent>
    </Dialog>
  );
}
